import type { HouseholdUpdateInput } from "@/types/household"
import { memo, useEffect, useId, useMemo } from "react"

import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import type { Resolver } from "react-hook-form"

import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Portal,
  Switch,
  Textarea,
} from "@chakra-ui/react"

import { Field } from "@/components/atoms/Field"

const skipLinkStyle = {
  position: "absolute",
  left: "-9999px",
  top: "-9999px",
  background: "#3182ce",
  color: "white",
  padding: "8px 16px",
  textDecoration: "none",
  borderRadius: "6px",
  zIndex: 1000,
} as const

type HouseholdFormValues = HouseholdUpdateInput

interface HouseholdFormDialogProps {
  isOpen: boolean
  title: string
  submitLabel: string
  resolver: Resolver<HouseholdFormValues>
  defaultValues: HouseholdFormValues
  onSubmit: (values: HouseholdFormValues) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  cancelLabel?: string
  finalFocusRef?: React.RefObject<HTMLElement | null>
  showSkipLink?: boolean
  loadingText?: string
}

const HouseholdFormDialog = memo(
  ({
    isOpen,
    title,
    submitLabel,
    resolver,
    defaultValues,
    onSubmit,
    onClose,
    isSubmitting = false,
    cancelLabel = "Cancel",
    finalFocusRef,
    showSkipLink = false,
    loadingText,
  }: HouseholdFormDialogProps) => {
    const formSectionId = useId()

    const normalizedDefaults = useMemo<HouseholdFormValues>(
      () => ({
        user_id: defaultValues.user_id ?? null,
        parent_first_name: defaultValues.parent_first_name ?? "",
        parent_last_name: defaultValues.parent_last_name ?? "",
        email: defaultValues.email ?? "",
        phone: defaultValues.phone ?? null,
        secondary_phone: defaultValues.secondary_phone ?? null,
        address: defaultValues.address ?? null,
        emergency_contact_name: defaultValues.emergency_contact_name ?? null,
        emergency_contact_phone: defaultValues.emergency_contact_phone ?? null,
        emergency_contact_relationship:
          defaultValues.emergency_contact_relationship ?? null,
        doctor_first_name: defaultValues.doctor_first_name ?? null,
        doctor_last_name: defaultValues.doctor_last_name ?? null,
        doctor_phone: defaultValues.doctor_phone ?? null,
        insurance_provider: defaultValues.insurance_provider ?? null,
        referral_source: defaultValues.referral_source ?? null,
        agreed_to_policy: defaultValues.agreed_to_policy ?? false,
        agreed_to_hold_harmless: defaultValues.agreed_to_hold_harmless ?? false,
        policy_agreement_date: defaultValues.policy_agreement_date ?? null,
        billing_email: defaultValues.billing_email ?? null,
        auto_pay_enabled: defaultValues.auto_pay_enabled ?? false,
        notes: defaultValues.notes ?? null,
        is_active: defaultValues.is_active ?? false,
      }),
      [defaultValues],
    )

    const form = useForm<HouseholdFormValues>({
      resolver,
      mode: "onBlur",
      defaultValues: normalizedDefaults,
    })

    useEffect(() => {
      if (isOpen) {
        void form.reset(normalizedDefaults)
      }
    }, [form, isOpen, normalizedDefaults])

    const handleClose = () => {
      form.reset(normalizedDefaults)
      onClose()
    }

    const handleSubmit: SubmitHandler<HouseholdFormValues> = async (values) => {
      try {
        await onSubmit(values)
        handleClose()
      } catch (error) {
        // Error handling is delegated to the mutation layer
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            handleClose()
          }
        }}
        size={{ base: "full", md: "xl" }}
        trapFocus
        restoreFocus
        initialFocusEl={() => null}
        finalFocusEl={() => finalFocusRef?.current ?? null}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent as="form" onSubmit={form.handleSubmit(handleSubmit)} maxH="90vh" display="flex" flexDirection="column">
              {showSkipLink && (
                <a
                  href={`#${formSectionId}`}
                  style={skipLinkStyle}
                  onFocus={(event) => {
                    event.currentTarget.style.left = "16px"
                    event.currentTarget.style.top = "16px"
                  }}
                  onBlur={(event) => {
                    event.currentTarget.style.left = "-9999px"
                    event.currentTarget.style.top = "-9999px"
                  }}
                >
                  Skip to form fields
                </a>
              )}

              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>

              <DialogBody id={formSectionId} overflowY="auto" flex="1">
                <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
                  <Field
                    invalid={!!form.formState.errors.user_id}
                    errorText={form.formState.errors.user_id?.message}
                    label="User Id"
                  >
                    <Input
                      placeholder={"Enter user id"}
                      aria-invalid={!!form.formState.errors.user_id}
                      {...form.register("user_id")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.parent_first_name}
                    errorText={form.formState.errors.parent_first_name?.message}
                    label="Parent First Name"
                  >
                    <Input
                      placeholder={"Enter parent first name"}
                      aria-invalid={!!form.formState.errors.parent_first_name}
                      {...form.register("parent_first_name")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.parent_last_name}
                    errorText={form.formState.errors.parent_last_name?.message}
                    label="Parent Last Name"
                  >
                    <Input
                      placeholder={"Enter parent last name"}
                      aria-invalid={!!form.formState.errors.parent_last_name}
                      {...form.register("parent_last_name")}
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!form.formState.errors.email}
                    errorText={form.formState.errors.email?.message}
                    label="Email"
                  >
                    <Input
                      placeholder={"Enter email"}
                      aria-invalid={!!form.formState.errors.email}
                      {...form.register("email")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.phone}
                    errorText={form.formState.errors.phone?.message}
                    label="Phone"
                  >
                    <Input
                      placeholder={"Enter phone"}
                      aria-invalid={!!form.formState.errors.phone}
                      {...form.register("phone")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.secondary_phone}
                    errorText={form.formState.errors.secondary_phone?.message}
                    label="Secondary Phone"
                  >
                    <Input
                      placeholder={"Enter secondary phone"}
                      aria-invalid={!!form.formState.errors.secondary_phone}
                      {...form.register("secondary_phone")}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.address}
                      errorText={form.formState.errors.address?.message}
                      label="Address"
                    >
                      <Textarea
                        {...form.register("address")}
                        placeholder={"Enter address"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.address}
                      />
                    </Field>
                  </Box>

                  <Field
                    invalid={!!form.formState.errors.emergency_contact_name}
                    errorText={
                      form.formState.errors.emergency_contact_name?.message
                    }
                    label="Emergency Contact Name"
                  >
                    <Input
                      placeholder={"Enter emergency contact name"}
                      aria-invalid={
                        !!form.formState.errors.emergency_contact_name
                      }
                      {...form.register("emergency_contact_name")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.emergency_contact_phone}
                    errorText={
                      form.formState.errors.emergency_contact_phone?.message
                    }
                    label="Emergency Contact Phone"
                  >
                    <Input
                      placeholder={"Enter emergency contact phone"}
                      aria-invalid={
                        !!form.formState.errors.emergency_contact_phone
                      }
                      {...form.register("emergency_contact_phone")}
                    />
                  </Field>

                  <Field
                    invalid={
                      !!form.formState.errors.emergency_contact_relationship
                    }
                    errorText={
                      form.formState.errors.emergency_contact_relationship
                        ?.message
                    }
                    label="Emergency Contact Relationship"
                  >
                    <Input
                      placeholder={"Enter emergency contact relationship"}
                      aria-invalid={
                        !!form.formState.errors.emergency_contact_relationship
                      }
                      {...form.register("emergency_contact_relationship")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.doctor_first_name}
                    errorText={form.formState.errors.doctor_first_name?.message}
                    label="Doctor First Name"
                  >
                    <Input
                      placeholder={"Enter doctor first name"}
                      aria-invalid={!!form.formState.errors.doctor_first_name}
                      {...form.register("doctor_first_name")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.doctor_last_name}
                    errorText={form.formState.errors.doctor_last_name?.message}
                    label="Doctor Last Name"
                  >
                    <Input
                      placeholder={"Enter doctor last name"}
                      aria-invalid={!!form.formState.errors.doctor_last_name}
                      {...form.register("doctor_last_name")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.doctor_phone}
                    errorText={form.formState.errors.doctor_phone?.message}
                    label="Doctor Phone"
                  >
                    <Input
                      placeholder={"Enter doctor phone"}
                      aria-invalid={!!form.formState.errors.doctor_phone}
                      {...form.register("doctor_phone")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.insurance_provider}
                    errorText={
                      form.formState.errors.insurance_provider?.message
                    }
                    label="Insurance Provider"
                  >
                    <Input
                      placeholder={"Enter insurance provider"}
                      aria-invalid={!!form.formState.errors.insurance_provider}
                      {...form.register("insurance_provider")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.referral_source}
                    errorText={form.formState.errors.referral_source?.message}
                    label="Referral Source"
                  >
                    <Input
                      placeholder={"Enter referral source"}
                      aria-invalid={!!form.formState.errors.referral_source}
                      {...form.register("referral_source")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.agreed_to_policy}
                    errorText={form.formState.errors.agreed_to_policy?.message}
                    label="Agreed To Policy"
                  >
                    <Controller
                      name="agreed_to_policy"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="agreed_to_policy"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Agreed To Policy"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.agreed_to_hold_harmless}
                    errorText={
                      form.formState.errors.agreed_to_hold_harmless?.message
                    }
                    label="Agreed To Hold Harmless"
                  >
                    <Controller
                      name="agreed_to_hold_harmless"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="agreed_to_hold_harmless"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Agreed To Hold Harmless"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.policy_agreement_date}
                    errorText={
                      form.formState.errors.policy_agreement_date?.message
                    }
                    label="Policy Agreement Date"
                  >
                    <Input
                      type="datetime-local"
                      placeholder={"Enter policy agreement date"}
                      aria-invalid={
                        !!form.formState.errors.policy_agreement_date
                      }
                      {...form.register("policy_agreement_date")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.billing_email}
                    errorText={form.formState.errors.billing_email?.message}
                    label="Billing Email"
                  >
                    <Input
                      placeholder={"Enter billing email"}
                      aria-invalid={!!form.formState.errors.billing_email}
                      {...form.register("billing_email")}
                    />
                  </Field>

                  <Field
                    invalid={!!form.formState.errors.auto_pay_enabled}
                    errorText={form.formState.errors.auto_pay_enabled?.message}
                    label="Auto Pay Enabled"
                  >
                    <Controller
                      name="auto_pay_enabled"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="auto_pay_enabled"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Auto Pay Enabled"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>

                  <Box gridColumn={{ md: "1 / -1" }}>
                    <Field
                      invalid={!!form.formState.errors.notes}
                      errorText={form.formState.errors.notes?.message}
                      label="Notes"
                    >
                      <Textarea
                        {...form.register("notes")}
                        placeholder={"Enter notes"}
                        rows={3}
                        resize="vertical"
                        aria-invalid={!!form.formState.errors.notes}
                      />
                    </Field>
                  </Box>

                  <Field
                    invalid={!!form.formState.errors.is_active}
                    errorText={form.formState.errors.is_active?.message}
                    label="Is Active"
                  >
                    <Controller
                      name="is_active"
                      control={form.control}
                      render={({ field: controllerField }) => (
                        <Switch.Root
                          id="is_active"
                          checked={!!controllerField.value}
                          onCheckedChange={({ checked }) =>
                            controllerField.onChange(checked)
                          }
                          aria-label="Is Active"
                        >
                          <Switch.HiddenInput
                            ref={controllerField.ref}
                            name={controllerField.name}
                            onBlur={controllerField.onBlur}
                          />
                          <Switch.Control>
                            <Switch.Thumb />
                          </Switch.Control>
                        </Switch.Root>
                      )}
                    />
                  </Field>
                </Box>
              </DialogBody>

              <DialogFooter>
                <HStack gap={3}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="submit"
                    colorPalette="blue"
                    loading={isSubmitting}
                    loadingText={loadingText ?? `${submitLabel}...`}
                  >
                    {submitLabel}
                  </Button>
                </HStack>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

HouseholdFormDialog.displayName = "HouseholdFormDialog"

export default HouseholdFormDialog
